import { useCallback, useEffect, useRef, useState } from 'react';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import {
  NOTIFICATIONS_ENDPOINTS,
  isNotificationsUnavailableError,
  isUnauthorizedError,
  normalizePreferences,
  serializePreferences,
} from '../_helpers/notifications.js';
import { useT } from '../contexts/LocaleContext.jsx';
import { englishT } from '../locales/index.js';

/** Catalog keys of the hook's error messages; `error` / `saveError.message` carry them resolved in the active locale. */
export const PREFERENCES_MESSAGE_KEYS = Object.freeze({
  loadError: 'notifications.preferences.loadError',
  saveError: 'notifications.preferences.saveError',
});

/* English copy of the messages above, for tests and callers without a locale. */
export const PREFERENCES_LOAD_ERROR = englishT(PREFERENCES_MESSAGE_KEYS.loadError);
export const PREFERENCES_SAVE_ERROR = englishT(PREFERENCES_MESSAGE_KEYS.saveError);
/** How long the "Saved" confirmation stays up. */
export const SAVED_NOTICE_MS = 2500;

export const PREFERENCES_STATUS = Object.freeze({
  IDLE: 'idle',
  LOADING: 'loading',
  READY: 'ready',
  ERROR: 'error',
  UNAVAILABLE: 'unavailable',
  UNAUTHORIZED: 'unauthorized',
});

/**
 * The listener's notification preferences (ENG-26): exactly the events and
 * per-event channels `GET /me/notifications/preferences` lists, each toggled
 * through `PUT` with the whole set. A toggle applies at once, and a failed
 * save puts it back and hands back a retry for the same change. Nothing is
 * invented locally: no event or channel exists here until the backend names
 * it, and push is never switchable while the web-push contract is missing
 * (see describePushAvailability).
 *
 * A preference is one (event, channel) cell — `setChannelEnabled` takes both
 * — because that is the only thing the backend stores (ENG-409). There is no
 * toggle for a whole channel or a whole event to offer.
 */
export function useNotificationPreferences({ enabled = true, pushOptions } = {}) {
  const fetchWrapperRef = useRef(useFetchWrapper());
  // The active locale's `t`, read through a ref so the load effect and the
  // save callback need not re-run when the listener switches language.
  const t = useT();
  const tRef = useRef(t);
  tRef.current = t;

  const [preferences, setPreferences] = useState(null);
  const [status, setStatus] = useState(enabled ? PREFERENCES_STATUS.LOADING : PREFERENCES_STATUS.IDLE);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const preferencesRef = useRef(preferences);
  preferencesRef.current = preferences;
  const savedTimerRef = useRef(null);
  const pushOptionsRef = useRef(pushOptions);
  pushOptionsRef.current = pushOptions;

  useEffect(() => {
    if (!enabled) {
      setStatus(PREFERENCES_STATUS.IDLE);
      setPreferences(null);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      setStatus(PREFERENCES_STATUS.LOADING);
      setError(null);
      try {
        const response = await fetchWrapperRef.current.get(NOTIFICATIONS_ENDPOINTS.preferences());
        if (cancelled) return;
        setPreferences(normalizePreferences(response, pushOptionsRef.current, tRef.current));
        setStatus(PREFERENCES_STATUS.READY);
      } catch (err) {
        if (cancelled) return;
        setPreferences(null);
        if (isNotificationsUnavailableError(err)) {
          setStatus(PREFERENCES_STATUS.UNAVAILABLE);
        } else if (isUnauthorizedError(err)) {
          setStatus(PREFERENCES_STATUS.UNAUTHORIZED);
        } else {
          console.error('Failed to load notification preferences:', err);
          setError(tRef.current(PREFERENCES_MESSAGE_KEYS.loadError));
          setStatus(PREFERENCES_STATUS.ERROR);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [enabled, reloadKey]);

  useEffect(() => () => clearTimeout(savedTimerRef.current), []);

  const flashSaved = useCallback(() => {
    clearTimeout(savedTimerRef.current);
    setSaved(true);
    savedTimerRef.current = setTimeout(() => setSaved(false), SAVED_NOTICE_MS);
  }, []);

  /** Persist `next`; on failure show `before` again and offer the same save. */
  const save = useCallback(async (next, before) => {
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    setPreferences(next);
    try {
      const response = await fetchWrapperRef.current.put(NOTIFICATIONS_ENDPOINTS.preferences(), serializePreferences(next));
      // The backend's answer is the saved state; an empty 204 means "as sent".
      setPreferences(response && typeof response === 'object' ? normalizePreferences(response, pushOptionsRef.current, tRef.current) : next);
      setSaving(false);
      flashSaved();
      return true;
    } catch (err) {
      console.error('Failed to save notification preferences:', err);
      setPreferences(before);
      setSaving(false);
      setSaveError({ message: tRef.current(PREFERENCES_MESSAGE_KEYS.saveError), retry: () => save(next, before) });
      return false;
    }
  }, [flashSaved]);

  /** Switch one (event, channel) cell; resolves false when there was nothing to send. */
  const setChannelEnabled = useCallback((eventId, channelId, enabledValue) => {
    const before = preferencesRef.current;
    if (!before) return Promise.resolve(false);
    const event = before.events?.find((item) => item.id === eventId);
    const channel = event?.channels?.find((item) => item.id === channelId);
    // Unknown, unavailable or unchanged: nothing to send.
    if (!channel || channel.available === false || channel.enabled === Boolean(enabledValue)) return Promise.resolve(false);
    const next = {
      ...before,
      events: before.events.map((item) => (item.id !== eventId ? item : {
        ...item,
        channels: item.channels.map((entry) => (entry.id === channelId ? { ...entry, enabled: Boolean(enabledValue) } : entry)),
      })),
    };
    return save(next, before);
  }, [save]);

  const reload = useCallback(() => setReloadKey((key) => key + 1), []);
  const dismissSaveError = useCallback(() => setSaveError(null), []);

  return {
    preferences,
    status,
    error,
    saving,
    saveError,
    saved,
    setChannelEnabled,
    reload,
    dismissSaveError,
  };
}
